import type { OfflineSummary } from './claim';
import type { FeedbackDraft } from './feedbackReport';
import type { GearSlot, Rarity, SaveData, SaveFile, SlotId, SubSpecId } from '../model/types';
import { browserStorage, createSimulatedClock, type ServerClock, type StorageLike } from './clock';
import type { FarmQueue } from './farmQueue';

const SAVE_KEY = 'threadbound.save.v1';

export interface AccountState {
  mode: 'local' | 'cloud';
  label: string;
  anonymous: boolean;
  readOnly: boolean;
  notice: string | null;
  collision: boolean;
}

export type BackendEvent =
  | { type: 'account'; account: AccountState }
  | { type: 'reloaded'; file: SaveFile; reason: 'save_conflict' | 'superseded' };

export interface SaveBackend {
  readonly offlineClaims: 'local' | 'server';
  load(): SaveFile | null;
  commit(expectedVersion: number, data: SaveData): SaveFile;
  serverNow(): number;
  advanceClock(ms: number): void;
  clear(): void;
  account(): AccountState;
  setStartupNotice?(message: string): void;
  prepare?(): Promise<void>;
  subscribe?(listener: (event: BackendEvent) => void): () => void;
  signInWithGoogle?(): Promise<void>;
  keepGoogleSave?(): Promise<void>;
  stayGuest?(): void;
  signOutAccount?(): Promise<void>;
  playHere?(): Promise<void>;
  markAway?(): void;
  markBack?(): Promise<OfflineSummary | null>;
  consumeOfflineSummary?(): OfflineSummary | null;
  flush?(): Promise<void>;
  dispose?(): void;
  /** Re-sim the server farm stage and write idle_snapshots. */
  commitLoadout?(): Promise<void>;
  /** Re-sim a clear and mint progress, currency, and drops. */
  validateClear?(stageId: number): Promise<SaveFile>;
  /**
   * One settle of queued repeat-farm clears.
   * `keepalive` is a best-effort send on page hide. Auth stays on the request.
   */
  settleFarmBatch?(
    request: { stageId: number; count: number; seq: number },
    options?: { keepalive?: boolean },
  ): Promise<SaveFile>;
  /** Unsent repeat-farm batch. The next cloud prepare sends it before claim_offline. */
  readFarmQueue?(): FarmQueue | null;
  writeFarmQueue?(queue: FarmQueue | null): void;
  /** Server-owned rank spend, re-braid, or spec change. `keepalive` lets the hide request finish as the tab closes. */
  applySpend?(request: SpendRequest, options?: { keepalive?: boolean }): Promise<SaveFile>;
  /** Server-owned push, drill, or stop-drill. Does not mint a clear. */
  applyFrontier?(request: FrontierRequest): Promise<SaveFile>;
  /** Server-owned equip, unequip, salvage, enhance, lock, or recycle. */
  gearAction?(request: GearRequest): Promise<SaveFile>;
  /** Insert one bug or idea report for the signed-in user. */
  submitFeedback?(report: FeedbackDraft): Promise<void>;
}

export interface GearRequest {
  action: 'equip' | 'unequip' | 'salvage' | 'enhance' | 'lock' | 'unlock' | 'recycle_bulk' | 'set_auto_recycle';
  itemId?: string;
  slot?: GearSlot;
  /** Bulk threshold, or the auto-recycle line. Null turns auto-recycle off. */
  rarity?: Rarity | null;
}

export interface SpendRequest {
  action: 'spend' | 'rebraid' | 'set_spec';
  slot?: SlotId;
  spec?: SubSpecId | null;
  /** Whole batch for one spend_points call. Absent on a single-slot spend. */
  ranks?: { slot: SlotId; count: number }[];
}

export interface FrontierRequest {
  action: 'push' | 'drill' | 'cancel_drill';
  stageId?: number;
}

export function createLocalBackend(storage: StorageLike = browserStorage(), clock?: ServerClock): SaveBackend {
  const time = clock ?? createSimulatedClock(storage);
  let notice: string | null = null;

  function read(): SaveFile | null {
    const raw = storage.getItem(SAVE_KEY);
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw) as SaveFile;
      if (!parsed || typeof parsed.version !== 'number' || !parsed.data) return null;
      return parsed;
    } catch {
      return null;
    }
  }

  return {
    offlineClaims: 'local',
    account() {
      return {
        mode: 'local',
        label: 'Local save',
        anonymous: false,
        readOnly: false,
        notice,
        collision: false,
      };
    },
    setStartupNotice(message: string) {
      notice = message;
    },
    load() {
      const file = read();
      return file ? structuredClone(file) : null;
    },
    commit(expectedVersion, data) {
      const current = read();
      if (current && current.version !== expectedVersion) {
        throw new Error('save_conflict');
      }
      const next: SaveFile = {
        version: current ? current.version + 1 : 1,
        data: structuredClone(data),
      };
      storage.setItem(SAVE_KEY, JSON.stringify(next));
      return structuredClone(next);
    },
    serverNow: () => time.now(),
    advanceClock: (ms) => time.advance(ms),
    clear() {
      storage.removeItem(SAVE_KEY);
    },
  };
}
